import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, ArrowRight, Lock, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { mockComplete } from "../lib/billing";
import { api } from "../lib/api";

/**
 * Post-checkout callback.
 * - ?mock=1 → we're running against mocked gateway; auto-simulate success.
 * - ?gw=<url> (no mock) → redirect to the real gateway hosted checkout.
 * - Otherwise → show a status page with the result.
 */
export default function CheckoutCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const subId = params.get("sub_id");
  const txnId = params.get("txn_id");
  const isMock = params.get("mock") === "1";
  const isPeach = params.get("peach") === "1";
  const gwUrl = params.get("gw");
  const status = params.get("status") || (isMock ? "processing" : null);

  const [state, setState] = useState(
    isMock
      ? "processing"
      : isPeach
        ? "confirming" // actively confirm with the gateway before declaring success
        : status === "canceled"
          ? "canceled"
          : "redirecting",
  );
  const [detail, setDetail] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (isMock && (subId || txnId)) {
        try {
          const res = await mockComplete({ sub_id: subId, txn_id: txnId });
          setDetail(res);
          setState("success");
          toast.success(
            subId
              ? "Thank you for your support — your subscription is active!"
              : "Thank you — credits added to your account.",
          );
        } catch (e) {
          console.error(e);
          setState("failed");
          toast.error(e.response?.data?.detail || e.message);
        }
        return;
      }
      if (gwUrl && !isMock) {
        // Real gateway: bounce to the hosted checkout URL.
        window.location.href = gwUrl;
        return;
      }
      // Returned from Peach Embedded Checkout — Peach's `onCompleted`
      // fires immediately on the client once the shopper finishes paying,
      // but OUR authoritative confirmation only happens once we've verified
      // with Peach server-side (webhook, or this sync-status poll). Poll a
      // few times with a short gap — the backend itself also retries a few
      // times per call — so the vast majority of payments resolve here
      // instead of leaving the user on a stale "pending" screen.
      if (isPeach && (subId || txnId)) {
        let resolved = false;
        let failed = false;
        for (let attempt = 0; attempt < 5 && !resolved && !failed; attempt++) {
          try {
            const { data } = await api.post("/billing/peach/sync-status", {
              sub_id: subId || undefined,
              txn_id: txnId || undefined,
            });
            if (data.outcome === "success" || data.status === "completed" || data.status === "active") {
              resolved = true;
              break;
            }
            if (data.outcome === "failed" || data.status === "failed") {
              failed = true;
              break;
            }
          } catch (e) {
            // Non-fatal: webhook may still arrive. Keep retrying.
            console.warn("Peach sync-status attempt failed", e);
          }
          if (attempt < 4) await new Promise((r) => setTimeout(r, 1500));
        }
        if (failed) {
          setState("failed");
          return;
        }
        // Whether resolved here or not, Peach's client-side onCompleted
        // already fired (money moved) — the background reconciliation
        // sweep will catch anything we couldn't confirm within this
        // window, so we still land on the success screen with a fresh
        // balance pull rather than stranding the user on "pending".
        setState("success");
      }
      // Returned from a real gateway with a status query param.
      if (status === "success" || status === "activated") setState("success");
      else if (status === "canceled" || status === "failed") setState(status);
      else setState("success");
      // Refresh the credit balance so we can show the new total.
      try {
        const { data } = await api.get("/usage/me");
        setBalance(data);
      } catch {
        /* ignore */
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-[720px] mx-auto px-6 md:px-10 py-20" data-testid="checkout-callback">
      <div className="hairline bg-white p-8 md:p-12 text-center">
        {state === "processing" && (
          <>
            <CircleNotch size={36} weight="bold" className="animate-spin mx-auto text-[var(--accent)]" />
            <div className="font-display font-bold text-xl mt-6">
              Simulating gateway (mocked)…
            </div>
            <p className="text-[var(--fg-2)] mt-2 text-sm">
              Real Peach Payments keys aren&apos;t wired up yet — we&apos;re completing the
              flow locally so you can test the UX end-to-end.
            </p>
          </>
        )}

        {state === "redirecting" && (
          <>
            <Lock size={36} weight="bold" className="mx-auto text-[var(--accent)]" />
            <div className="font-display font-bold text-xl mt-6">
              Redirecting to secure checkout…
            </div>
            <p className="text-[var(--fg-2)] mt-2 text-sm">
              If nothing happens in 5 seconds, click the button below.
            </p>
            {gwUrl && (
              <a href={gwUrl} className="btn-accent mt-6 inline-flex items-center gap-2">
                Continue to gateway <ArrowRight size={14} weight="bold" />
              </a>
            )}
          </>
        )}

        {state === "confirming" && (
          <>
            <CircleNotch size={36} weight="bold" className="animate-spin mx-auto text-[var(--accent)]" data-testid="checkout-confirming-spinner" />
            <div className="font-display font-bold text-xl mt-6">
              Confirming your payment…
            </div>
            <p className="text-[var(--fg-2)] mt-2 text-sm">
              Checking with Peach Payments now — this only takes a few seconds.
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle size={44} weight="fill" className="mx-auto text-[var(--success)]" />
            <div className="font-display font-black text-3xl mt-6" data-testid="checkout-success-title">
              {balance && !balance.unlimited
                ? `${balance.conversions_remaining} conversions in your account`
                : "Conversions added!"}
            </div>
            <p className="text-[var(--fg-2)] mt-3" data-testid="checkout-success-message">
              Your pack purchase was successful and your conversions have been added
              to your account. Paid conversions never expire — start converting whenever
              you&apos;re ready.
            </p>
            {balance && !balance.unlimited && (
              <div className="mt-5 inline-flex items-center gap-4 hairline px-5 py-3" data-testid="callback-balance">
                <span className="font-display font-black text-2xl">
                  {balance.conversions_remaining}
                </span>
                <span className="text-xs font-mono text-[var(--fg-2)]">
                  conversions left · {balance.free_credits ?? 0} free + {balance.paid_credits ?? 0} paid
                </span>
              </div>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/billing" className="btn-primary" data-testid="callback-billing-link">
                View billing
              </Link>
              <Link to="/convert" className="btn-accent" data-testid="callback-convert-link">
                Start converting <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </>
        )}

        {(state === "failed" || state === "canceled") && (
          <>
            <XCircle size={44} weight="fill" className="mx-auto text-[var(--accent)]" />
            <div className="font-display font-black text-3xl mt-6">
              {state === "canceled" ? "Checkout canceled" : "Payment failed"}
            </div>
            <p className="text-[var(--fg-2)] mt-3">
              {state === "canceled"
                ? "No worries — nothing was charged."
                : "We couldn't complete the payment. Try another card or gateway."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/pricing" className="btn-accent">
                Try again <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/billing" className="btn-ghost">
                Back to billing
              </Link>
            </div>
          </>
        )}
      </div>
      <div className="text-center text-[10px] font-mono text-[var(--fg-2)] tracking-widest mt-6">
        PEACH PAYMENTS PRIMARY · PAYFAST FALLBACK · POPIA COMPLIANT
      </div>
    </div>
  );
}
